import { zodResolver } from "@hookform/resolvers/zod";
import type { TFunction } from "i18next";
import { AlertTriangle, Copy } from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import { Checkbox } from "@/components/ui/v2/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { useDuplicateSegment } from "@/features/segments/api/useDuplicateSegment";
import { getNameErrorMessage } from "@/features/segments/builder/api/saveErrors";
import { useSegmentPaths } from "@/features/segments/builder/hooks/useSegmentPaths";
import {
  SEGMENT_NAME_MAX_LENGTH,
  segmentCopyOptionsSchema,
  segmentNameSchema,
  type SegmentCopyOptions,
} from "@/features/segments/builder/schemas";
import type { SegmentDetailResponse } from "@/features/segments/types";
import useToast from "@/lib/hooks/useToast";

const COPY_OPTIONS = segmentCopyOptionsSchema.keyof().options;

const createDuplicateSchema = (t: TFunction) =>
  z.object({
    name: segmentNameSchema(t),
    copy: segmentCopyOptionsSchema,
  });

const duplicateName = (t: TFunction, name: string) => {
  const overflow = t("admin.segments.duplicate.name", { name }).length - SEGMENT_NAME_MAX_LENGTH;
  const fittingName = overflow > 0 ? name.slice(0, name.length - overflow).trimEnd() : name;
  return t("admin.segments.duplicate.name", { name: fittingName });
};

type DuplicateFormValues = z.infer<ReturnType<typeof createDuplicateSchema>>;

type DuplicateSegmentDialogProps = {
  segment: Pick<SegmentDetailResponse, "id" | "name">;
  copyable: SegmentCopyOptions;
  clientId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDuplicated?: () => void;
};

export function DuplicateSegmentDialog({
  segment,
  copyable,
  clientId,
  open,
  onOpenChange,
  onDuplicated,
}: DuplicateSegmentDialogProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const paths = useSegmentPaths();
  const duplicate = useDuplicateSegment();

  const form = useForm<DuplicateFormValues>({
    resolver: zodResolver(createDuplicateSchema(t)),
    values: {
      name: duplicateName(t, segment.name),
      copy: copyable,
    },
  });
  const copy = useWatch({ control: form.control, name: "copy" });
  const hasNothingToCopy = COPY_OPTIONS.every((option) => !copy[option]);
  const matchesEveryVacancy = copyable.rules && !copy.rules;

  const handleOpenChange = (nextOpen: boolean) => {
    if (duplicate.isPending) return;
    if (!nextOpen) {
      form.reset();
      duplicate.reset();
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = ({ name, copy: options }: DuplicateFormValues) => {
    duplicate.mutate(
      { segmentId: segment.id, clientId, name, copy: options },
      {
        onSuccess: (duplicateId) => {
          showToast(t("admin.segments.duplicate.success"), "success");
          onDuplicated?.();
          onOpenChange(false);
          navigate(paths.edit(duplicateId));
        },
        onError: (error) => {
          const message = getNameErrorMessage(t, error);
          if (!message) {
            showToast(t("admin.segments.duplicate.error"), "error");
            return;
          }
          form.setError("name", { message });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.segments.duplicate.dialog.title")}</DialogTitle>
          <DialogDescription className="text-wrap">
            {t("admin.segments.duplicate.dialog.description", { name: segment.name })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus onFocus={(event) => event.currentTarget.select()} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <fieldset className="flex flex-col gap-3">
              <legend className="mb-3 text-sm font-medium">
                {t("admin.segments.duplicate.dialog.copyLabel")}
              </legend>
              {COPY_OPTIONS.map((option) => (
                <FormField
                  key={option}
                  control={form.control}
                  name={`copy.${option}`}
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          disabled={!copyable[option]}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                        />
                      </FormControl>
                      <FormLabel className="cursor-pointer font-normal">
                        {t(`admin.segments.duplicate.dialog.copy.${option}`)}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
              {hasNothingToCopy && (
                <p className="text-sm text-muted-foreground">
                  {t("admin.segments.duplicate.dialog.atLeastOne")}
                </p>
              )}
              {matchesEveryVacancy && (
                <Alert variant="warning">
                  <AlertTriangle />
                  <AlertDescription>
                    {t("admin.segments.duplicate.dialog.noRulesWarning")}
                  </AlertDescription>
                </Alert>
              )}
            </fieldset>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={duplicate.isPending}
                onClick={() => handleOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                loading={duplicate.isPending}
                disabled={hasNothingToCopy}
              >
                <Copy />
                {t("admin.segments.duplicate.dialog.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
